import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import InventoryTable from './components/InventoryTable';
import Alerts from './components/Alerts';
import Snapshots from './components/Snapshots';
import FulfillmentForecast from './components/FulfillmentForecast';
import POReceipts from './components/POReceipts';
import WebSocketBadge from './components/WebSocketBadge';
import Exports from './components/Exports';
import BinLocations from './components/BinLocations';
import CycleCount from './components/CycleCount';
import KitProducts from './components/KitProducts';
import LoginPage from './components/LoginPage';
import Settings from './components/Settings';

// Protected route wrapper that checks authentication and role
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, user } = useAuth();

    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;

    return children;
};

function AppContent() {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) return null;

    if (!isAuthenticated) {
        return (
            <Routes>
                <Route path="*" element={<LoginPage />} />
            </Routes>
        );
    }

    const isPicker = user.role === 'picker';

    return (
        <div className={`app-container ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            <Sidebar isCollapsed={isSidebarCollapsed} toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)} user={user} />
            <main className="main-content" style={{ position: 'relative' }}>
                <WebSocketBadge />
                <Routes>
                    {/* All roles can access these */}
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/inventory" element={<InventoryTable />} />
                    <Route path="/kit-products" element={<KitProducts />} />
                    <Route path="/locations" element={<BinLocations />} />
                    <Route path="/cycle-count" element={<CycleCount />} />

                    {/* Admin and Manager only */}
                    <Route path="/forecast" element={
                        <ProtectedRoute allowedRoles={['admin', 'manager']}>
                            <FulfillmentForecast />
                        </ProtectedRoute>
                    } />
                    <Route path="/po-receipts" element={
                        <ProtectedRoute allowedRoles={['admin', 'manager']}>
                            <POReceipts />
                        </ProtectedRoute>
                    } />
                    <Route path="/snapshots" element={
                        <ProtectedRoute allowedRoles={['admin', 'manager']}>
                            <Snapshots />
                        </ProtectedRoute>
                    } />
                    <Route path="/exports" element={
                        <ProtectedRoute allowedRoles={['admin', 'manager']}>
                            <Exports />
                        </ProtectedRoute>
                    } />
                    <Route path="/alerts" element={
                        <ProtectedRoute allowedRoles={['admin', 'manager']}>
                            <Alerts />
                        </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                        <ProtectedRoute allowedRoles={['admin', 'manager']}>
                            <Settings />
                        </ProtectedRoute>
                    } />

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <AppContent />
            </Router>
        </AuthProvider>
    );
}

export default App;
